import { Loader2, Video } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SecurePlayer } from '@/components/video/SecurePlayer';
import { TimestampedNotes } from '@/components/video/TimestampedNotes';
import { getPlaybackUrls } from '@/services/videoService';
import { useAuthStore } from '@/store/authStore';

import { PreviewRow } from './PreviewRow';

import type { VideoSummary } from '@/types/submission.types';
import type { PlaybackUrlItem } from '@/types/video.types';

interface VideoTabProps {
  video?: VideoSummary;
  applicationId: string;
}

function formatDuration(totalSec: number): string {
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatStatus(status: string, t: (key: string) => string): string {
  if (status === 'uploaded' || status === 'submitted') {
    return t('submissionDetail.video.statusUploaded');
  }
  if (status === 'draft' || status === 'in_progress') {
    return t('submissionDetail.video.statusDraft');
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function VideoTab({ video, applicationId }: VideoTabProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [playbackUrls, setPlaybackUrls] = useState<PlaybackUrlItem[] | null>(null);
  const [isLoadingPlayback, setIsLoadingPlayback] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [activeResponseIdx, setActiveResponseIdx] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const watermarkText = user ? `${user.firstName} ${user.lastName} (${user.email})` : 'HR Reviewer';

  const handleLoadPlayback = useCallback(async () => {
    if (!video) return;
    setIsLoadingPlayback(true);
    setPlaybackError(null);
    try {
      const urls = await getPlaybackUrls(video.videoInterviewId);
      setPlaybackUrls(urls);
      setActiveResponseIdx(0);
      setCurrentTime(0);
    } catch {
      setPlaybackError(t('submissionDetail.video.errorPlayback'));
    } finally {
      setIsLoadingPlayback(false);
    }
  }, [video, t]);

  const handleSeekToTime = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  if (!video) {
    return (
      <div className='bg-white shadow rounded-lg p-12 flex flex-col items-center justify-center text-center'>
        <Video className='h-12 w-12 text-gray-300 mb-4' />
        <h3 className='text-lg font-medium text-gray-600 mb-2'>{t('submissionDetail.video.title')}</h3>
        <p className='text-sm text-gray-400'>{t('submissionDetail.video.noVideo')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Video info summary */}
      <div className='bg-white shadow rounded-lg overflow-hidden'>
        <div className='px-6 py-4 bg-gray-50 border-b border-gray-200'>
          <h4 className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>
            {t('submissionDetail.video.title')}
          </h4>
        </div>
        <div className='px-6 py-4 space-y-2'>
          <PreviewRow label={t('submissionDetail.video.status')} value={formatStatus(video.status, t)} />
          <PreviewRow label={t('submissionDetail.video.responses')} value={video.responseCount} />
          {video.durationTotalSec !== undefined ? (
            <PreviewRow label={t('submissionDetail.video.duration')} value={formatDuration(video.durationTotalSec)} />
          ) : null}
          <PreviewRow label={t('submissionDetail.video.submittedAt')} value={formatDate(video.submittedAt)} />
          <PreviewRow label={t('submissionDetail.video.createdAt')} value={formatDate(video.createdAt)} />
        </div>
      </div>

      {/* Load playback / Player */}
      {!playbackUrls ? (
        <div className='flex flex-col items-center gap-3'>
          {isLoadingPlayback ? (
            <div className='flex items-center gap-2 text-sm text-gray-500'>
              <Loader2 className='h-5 w-5 animate-spin text-blue-600' />
              <span>{t('submissionDetail.video.loadingPlayback')}</span>
            </div>
          ) : (
            <>
              {playbackError ? <p className='text-sm text-red-600'>{playbackError}</p> : null}
              <button
                type='button'
                onClick={() => void handleLoadPlayback()}
                className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors'>
                <Video className='h-4 w-4' />
                {t('submissionDetail.video.loadPlayback')}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className='space-y-4'>
          {/* Response tabs */}
          {playbackUrls.length > 1 ? (
            <div className='flex gap-2 flex-wrap'>
              {playbackUrls.map((item, index) => (
                <button
                  key={item.promptId}
                  type='button'
                  onClick={() => {
                    setActiveResponseIdx(index);
                    setCurrentTime(0);
                  }}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeResponseIdx === index
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {t('submissionDetail.video.response', { number: index + 1 })}
                </button>
              ))}
            </div>
          ) : null}

          {/* Secure video player */}
          {playbackUrls[activeResponseIdx] ? (
            <SecurePlayer
              src={playbackUrls[activeResponseIdx].url}
              title={t('submissionDetail.video.response', { number: activeResponseIdx + 1 })}
              watermarkText={watermarkText}
              onTimeUpdate={setCurrentTime}
              videoRef={videoRef}
            />
          ) : null}

          {/* Timestamped notes */}
          <TimestampedNotes applicationId={applicationId} currentTime={currentTime} onSeek={handleSeekToTime} />

          {/* Footer */}
          <p className='text-xs text-gray-400 text-center'>{t('submissionDetail.video.expiryNote')}</p>

          <div className='flex justify-center'>
            <button
              type='button'
              onClick={() => void handleLoadPlayback()}
              disabled={isLoadingPlayback}
              className='inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50'>
              {isLoadingPlayback ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Video className='h-3.5 w-3.5' />}
              {t('submissionDetail.video.reloadUrls')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
